import { COUNTRIES, getFlagEmoji } from '@/lib/countries';
import type { Locale } from '@/lib/i18n';

/** Normalized country code forwarded from proxy/middleware to SSR. */
export const GEO_COUNTRY_HEADER = 'x-geo-country';
export const GEO_COUNTRY_COOKIE = 'GEO_COUNTRY';

const INVALID_COUNTRY_CODES = new Set(['XX', 'T1']);

export type GeoCountry = {
  code: string;
  name: string | null;
  flag: string;
};

export function normalizeCountryCode(raw: string | null | undefined): string | null {
  const code = raw?.trim().toUpperCase() ?? '';
  if (!/^[A-Z]{2}$/.test(code) || INVALID_COUNTRY_CODES.has(code)) {
    return null;
  }
  return code;
}

export function resolveGeoCountry(code: string | null | undefined): GeoCountry | null {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return null;

  const name = COUNTRIES.find((country) => country.code === normalized)?.name ?? null;
  return {
    code: normalized,
    name,
    flag: getFlagEmoji(normalized),
  };
}

export function getGeoCountryFromRequestHeaders(
  requestHeaders: Headers,
): GeoCountry | null {
  const override = process.env.GEO_COUNTRY_OVERRIDE?.trim();
  if (override && process.env.NODE_ENV !== 'production') {
    return resolveGeoCountry(override);
  }

  const code =
    requestHeaders.get(GEO_COUNTRY_HEADER) ??
    requestHeaders.get('cf-ipcountry') ??
    requestHeaders.get('CF-IPCountry');

  return resolveGeoCountry(code);
}

export function getLocalizedCountryName(code: string, locale: Locale | string): string {
  const lang = String(locale).split('-')[0];
  try {
    const name = new Intl.DisplayNames([lang], { type: 'region' }).of(code.toUpperCase());
    if (name && name !== code) return name;
  } catch {
    // fall through
  }
  return resolveGeoCountry(code)?.name ?? code;
}
